// src/components/Notifications/NotificationCenter.tsx - Centre de notifications dans le header

import React, { useState } from 'react';
import { Alert, Badge, Drawer, Button, List, Typography, Tag, Space, Empty } from 'antd';
import { BellOutlined, WarningOutlined, ExclamationCircleOutlined, CloseOutlined, MobileOutlined } from '@ant-design/icons';
import { useNotifications, useMarkAllNotificationsAsRead } from '../../hooks/useNotifications';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { NOTIFICATION_COLORS } from '../../types/notifications';
import type { Notification } from '../../types/notifications';
import {
  getNotificationMessage,
  getNotificationTypeLabel,
} from '../../utils/i18n/getNotificationMessage';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';

import { formatCurrency } from '../../utils/numberFormat';

const { Text } = Typography;

/** Les clés i18n message portent déjà un emoji ; le Tag affiche l’icône → on évite le doublon. */
const stripLeadingNotificationEmoji = (message: string): string =>
  message.replace(/^(?:\p{Extended_Pictographic}\uFE0F?(?:\u200D\p{Extended_Pictographic}\uFE0F?)*)+\s*/u, '');

// Styles pour les animations
const notificationStyles = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  @keyframes shake {
    0%, 100% { transform: rotate(0deg); }
    10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
    20%, 40%, 60%, 80% { transform: rotate(10deg); }
  }

  .notification-bell-shake {
    animation: shake 0.6s ease-in-out infinite;
    transform-origin: top center;
  }
`;

// Ajouter les styles CSS dans le document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = notificationStyles;
  if (!document.head.querySelector('style[data-notification-styles]')) {
    styleSheet.setAttribute('data-notification-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}

interface NotificationCenterProps {
  className?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className }) => {
  const { t } = useAppTranslation('notifications');
  const { locale } = useLocale();
  const dateLocale = locale === 'en' ? enUS : fr;
  const isNarrow = useMediaQuery('(max-width: 576px)');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { data: notifications = [] } = useNotifications({ limit: 50 });
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const {
    permission,
    isSupported,
    isRegistered,
    isRegistering,
    vapidConfigured,
    enablePush,
  } = usePushNotifications();
  const navigate = useNavigate();
  const { currentBusiness, selectBusiness, switchToBusiness, isSuperAdmin, isCSM } = useAuth();

  const ensureNotificationBusiness = async (notification: Notification) => {
    const targetBiz = notification.businessId;
    if (!targetBiz || targetBiz === currentBusiness?.id) return;
    if (isSuperAdmin || isCSM) {
      await switchToBusiness(targetBiz);
    } else {
      await selectBusiness(targetBiz);
    }
  };

  const showPushOptIn =
    isSupported &&
    vapidConfigured &&
    !isRegistered &&
    permission !== 'denied';

  const unreadCount = notifications.filter(n => !n.read).length;
  const criticalCount = notifications.filter(n => n.severity === 'critical' && !n.read).length;
  const unreadAppointmentRequests = notifications.filter(n => n.type === 'appointment_request' && !n.read).length;

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.mutateAsync();
  };

  const handleOpenDrawer = async () => {
    setDrawerVisible(true);
    // Marquer toutes les notifications comme lues dès l'ouverture
    if (unreadCount > 0) {
      await handleMarkAllAsRead();
    }
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const handleViewProduct = async (notification: Notification) => {
    if (notification.type === 'low_stock' || notification.type === 'out_of_stock') {
      setDrawerVisible(false);
      await ensureNotificationBusiness(notification);
      navigate('/inventory/products', {
        state: {
          selectedProductId: notification.productId,
          openProductModal: true,
        },
      });
    }
  };

  const handleViewClient = async (notification: Notification) => {
    if (notification.type === 'birthday') {
      setDrawerVisible(false);
      await ensureNotificationBusiness(notification);
      navigate('/clients', {
        state: {
          selectedClientId: notification.clientId,
          openClientModal: true,
        },
      });
    }
  };

  const handleViewAppointment = async (notification: Notification) => {
    if (
      notification.type === 'appointment_reminder_24h' ||
      notification.type === 'appointment_reminder_2h'
    ) {
      setDrawerVisible(false);
      await ensureNotificationBusiness(notification);
      navigate('/appointments', {
        state: {
          selectedAppointmentId: notification.appointmentId,
          appointmentDate: notification.appointmentDate,
          openAppointmentModal: true,
        },
      });
    }
  };

  const handleViewAppointmentRequest = async (notification: Notification) => {
    if (notification.type === 'appointment_request') {
      setDrawerVisible(false);
      await ensureNotificationBusiness(notification);
      navigate('/appointment-requests');
    }
  };

  const handleViewPaymentPlan = async (notification: Notification) => {
    if (
      notification.type === 'payment_plan_reminder_2d' ||
      notification.type === 'payment_plan_reminder_1d' ||
      notification.type === 'payment_plan_reminder_due' ||
      notification.type === 'payment_plan_overdue'
    ) {
      setDrawerVisible(false);
      await ensureNotificationBusiness(notification);
      if (notification.paymentPlanId) {
        navigate(`/payment-plans?openPlan=${encodeURIComponent(notification.paymentPlanId)}`);
      } else {
        navigate('/payment-plans');
      }
    }
  };

  const handleViewPickupRequest = async (notification: Notification) => {
    if (notification.type !== 'pickup_request') return;
    setDrawerVisible(false);
    await ensureNotificationBusiness(notification);
    const id = notification.pickupRequestId;
    navigate(id ? `/pickups?open=${encodeURIComponent(id)}` : '/pickups');
  };

  const handleViewSupportTicket = async (notification: Notification) => {
    const id = notification.supportTicketId;
    setDrawerVisible(false);
    if (notification.type === 'support_ticket_created') {
      navigate(
        id
          ? `/super-admin/support-tickets?open=${encodeURIComponent(id)}`
          : '/super-admin/support-tickets'
      );
      return;
    }
    if (
      notification.type === 'support_ticket_reply' ||
      notification.type === 'support_ticket_resolved' ||
      notification.type === 'support_ticket_opened_for_you'
    ) {
      await ensureNotificationBusiness(notification);
      navigate(id ? `/aide?open=${encodeURIComponent(id)}` : '/aide');
    }
  };

  const handleViewActivityReport = async (notification: Notification) => {
    if (
      notification.type !== 'activity_report_submit_reminder' &&
      notification.type !== 'activity_report_validated' &&
      notification.type !== 'activity_report_overdue'
    ) {
      return;
    }
    setDrawerVisible(false);
    await ensureNotificationBusiness(notification);
    navigate('/activity/reports');
  };

  const openTypedRoute = async (notification: Notification, path: string) => {
    setDrawerVisible(false);
    await ensureNotificationBusiness(notification);
    navigate(path);
  };

  const isSupportNotification = (type: string) =>
    type === 'support_ticket_created' ||
    type === 'support_ticket_reply' ||
    type === 'support_ticket_resolved' ||
    type === 'support_ticket_opened_for_you';

  const isPaymentPlanReminder = (type: string) =>
    type === 'payment_plan_reminder_2d' ||
    type === 'payment_plan_reminder_1d' ||
    type === 'payment_plan_reminder_due' ||
    type === 'payment_plan_overdue';

  const isActivityReportNotification = (type: string) =>
    type === 'activity_report_submit_reminder' ||
    type === 'activity_report_validated' ||
    type === 'activity_report_overdue';

  const isLicencePaymentNotification = (type: string) =>
    type === 'licence_payment_due_7d' ||
    type === 'licence_payment_due_1d' ||
    type === 'licence_payment_blocked';

  const isMessagesClientsNotification = (type: string) =>
    type === 'messages_clients_ready' ||
    type === 'messages_clients_no_phone';

  const isManufacturingNotification = (type: string) =>
    type === 'manufacturing_order_on_hold' ||
    type === 'manufacturing_order_done';

  const isInventoryAuditNotification = (type: string) =>
    type === 'inventory_audit_started' ||
    type === 'inventory_audit_to_close';

  const isPerformanceNotification = (type: string) =>
    type === 'performance_sheets_generated' ||
    type === 'performance_sheet_validated';

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'low_stock':
        return <WarningOutlined style={{ color: NOTIFICATION_COLORS.low_stock }} />;
      case 'out_of_stock':
        return <ExclamationCircleOutlined style={{ color: NOTIFICATION_COLORS.out_of_stock }} />;
      case 'appointment_reminder_24h':
        return <span style={{ color: NOTIFICATION_COLORS.appointment_reminder_24h, fontSize: '16px' }}>📅</span>;
      case 'appointment_reminder_2h':
        return <span style={{ color: NOTIFICATION_COLORS.appointment_reminder_2h, fontSize: '16px' }}>⏰</span>;
      case 'birthday':
        return <span style={{ color: NOTIFICATION_COLORS.birthday, fontSize: '16px' }}>🎂</span>;
      case 'appointment_request':
        return <span style={{ color: '#52c41a', fontSize: '16px' }}>📞</span>;
      case 'pickup_request':
        return <span style={{ color: NOTIFICATION_COLORS.pickup_request, fontSize: '16px' }}>🧺</span>;
      case 'support_ticket_created':
        return <span style={{ color: NOTIFICATION_COLORS.support_ticket_created, fontSize: '16px' }}>🎧</span>;
      case 'support_ticket_reply':
        return <span style={{ color: NOTIFICATION_COLORS.support_ticket_reply, fontSize: '16px' }}>💬</span>;
      case 'support_ticket_resolved':
        return <span style={{ color: NOTIFICATION_COLORS.support_ticket_resolved, fontSize: '16px' }}>✅</span>;
      case 'support_ticket_opened_for_you':
        return <span style={{ color: NOTIFICATION_COLORS.support_ticket_opened_for_you, fontSize: '16px' }}>📩</span>;
      case 'payment_plan_reminder_2d':
        return <span style={{ color: NOTIFICATION_COLORS.payment_plan_reminder_2d, fontSize: '16px' }}>📅</span>;
      case 'payment_plan_reminder_1d':
        return <span style={{ color: NOTIFICATION_COLORS.payment_plan_reminder_1d, fontSize: '16px' }}>⏰</span>;
      case 'payment_plan_reminder_due':
      case 'payment_plan_overdue':
        return <span style={{ color: NOTIFICATION_COLORS.payment_plan_reminder_due, fontSize: '16px' }}>🚨</span>;
      case 'activity_report_submit_reminder':
        return <span style={{ color: NOTIFICATION_COLORS.activity_report_submit_reminder, fontSize: '16px' }}>📝</span>;
      case 'activity_report_validated':
        return <span style={{ color: NOTIFICATION_COLORS.activity_report_validated, fontSize: '16px' }}>✅</span>;
      case 'activity_report_overdue':
        return <span style={{ color: NOTIFICATION_COLORS.activity_report_overdue, fontSize: '16px' }}>⏰</span>;
      case 'performance_sheets_generated':
        return <span style={{ color: NOTIFICATION_COLORS.performance_sheets_generated, fontSize: '16px' }}>📊</span>;
      case 'performance_sheet_validated':
        return <span style={{ color: NOTIFICATION_COLORS.performance_sheet_validated, fontSize: '16px' }}>✅</span>;
      case 'licence_payment_due_7d':
        return <span style={{ color: NOTIFICATION_COLORS.licence_payment_due_7d, fontSize: '16px' }}>📅</span>;
      case 'licence_payment_due_1d':
        return <span style={{ color: NOTIFICATION_COLORS.licence_payment_due_1d, fontSize: '16px' }}>⏰</span>;
      case 'licence_payment_blocked':
        return <span style={{ color: NOTIFICATION_COLORS.licence_payment_blocked, fontSize: '16px' }}>🔒</span>;
      case 'messages_clients_ready':
        return <span style={{ color: NOTIFICATION_COLORS.messages_clients_ready, fontSize: '16px' }}>💬</span>;
      case 'messages_clients_no_phone':
        return <span style={{ color: NOTIFICATION_COLORS.messages_clients_no_phone, fontSize: '16px' }}>📱</span>;
      case 'manufacturing_order_on_hold':
        return <span style={{ color: NOTIFICATION_COLORS.manufacturing_order_on_hold, fontSize: '16px' }}>⏸️</span>;
      case 'manufacturing_order_done':
        return <span style={{ color: NOTIFICATION_COLORS.manufacturing_order_done, fontSize: '16px' }}>🏭</span>;
      case 'inventory_audit_started':
        return <span style={{ color: NOTIFICATION_COLORS.inventory_audit_started, fontSize: '16px' }}>📋</span>;
      case 'inventory_audit_to_close':
        return <span style={{ color: NOTIFICATION_COLORS.inventory_audit_to_close, fontSize: '16px' }}>✅</span>;
      case 'inventory_loss_created':
        return <span style={{ color: NOTIFICATION_COLORS.inventory_loss_created, fontSize: '16px' }}>📉</span>;
      default:
        return <BellOutlined />;
    }
  };

  const getNotificationColor = (type: string) => {
    return NOTIFICATION_COLORS[type as keyof typeof NOTIFICATION_COLORS] || '#1890ff';
  };

  const formatTime = (timestamp: Date) => {
    return formatDistanceToNow(timestamp, { addSuffix: true, locale: dateLocale });
  };

  const isNavigableNotification = (type: string) =>
    isSupportNotification(type) ||
    type === 'pickup_request' ||
    type === 'appointment_request' ||
    isPaymentPlanReminder(type) ||
    isActivityReportNotification(type) ||
    isLicencePaymentNotification(type) ||
    isMessagesClientsNotification(type) ||
    isManufacturingNotification(type) ||
    isInventoryAuditNotification(type) ||
    isPerformanceNotification(type) ||
    type === 'inventory_loss_created' ||
    type === 'low_stock' ||
    type === 'out_of_stock' ||
    type === 'birthday' ||
    type === 'appointment_reminder_24h' ||
    type === 'appointment_reminder_2h';

  const handleNotificationNavigate = (notification: Notification) => {
    if (isSupportNotification(notification.type)) {
      void handleViewSupportTicket(notification);
    } else if (notification.type === 'pickup_request') {
      void handleViewPickupRequest(notification);
    } else if (notification.type === 'appointment_request') {
      void handleViewAppointmentRequest(notification);
    } else if (isPaymentPlanReminder(notification.type)) {
      void handleViewPaymentPlan(notification);
    } else if (isActivityReportNotification(notification.type)) {
      void handleViewActivityReport(notification);
    } else if (isPerformanceNotification(notification.type)) {
      void openTypedRoute(notification, '/performance-indicators');
    } else if (isLicencePaymentNotification(notification.type)) {
      void openTypedRoute(notification, '/mon-paiement');
    } else if (isMessagesClientsNotification(notification.type)) {
      void openTypedRoute(notification, '/messages-clients');
    } else if (isManufacturingNotification(notification.type)) {
      void openTypedRoute(notification, '/manufacturing/orders');
    } else if (isInventoryAuditNotification(notification.type)) {
      void openTypedRoute(notification, '/inventory/audit');
    } else if (notification.type === 'inventory_loss_created') {
      void openTypedRoute(notification, '/inventory/losses');
    } else if (notification.type === 'low_stock' || notification.type === 'out_of_stock') {
      void handleViewProduct(notification);
    } else if (notification.type === 'birthday') {
      void handleViewClient(notification);
    } else if (
      notification.type === 'appointment_reminder_24h' ||
      notification.type === 'appointment_reminder_2h'
    ) {
      void handleViewAppointment(notification);
    }
  };

  const renderNotificationMeta = (notification: Notification) => {
    if (notification.type === 'low_stock' || notification.type === 'out_of_stock') {
      return (
        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {notification.siteName
            ? t('meta.productSite', {
                productName: notification.productName,
                currentStock: notification.currentStock,
                siteName: notification.siteName,
              })
            : t('meta.product', {
                productName: notification.productName,
                currentStock: notification.currentStock,
              })}
        </Text>
      );
    }
    if (notification.type === 'birthday') {
      return (
        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {t('meta.client', { clientName: notification.clientName })}
        </Text>
      );
    }
    if (
      notification.type === 'appointment_reminder_24h' ||
      notification.type === 'appointment_reminder_2h'
    ) {
      return (
        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {t('meta.appointment', {
            clientName: notification.clientName,
            appointmentTitle: notification.appointmentTitle,
            appointmentTime: notification.appointmentTime,
          })}
        </Text>
      );
    }
    if (isPaymentPlanReminder(notification.type)) {
      return (
        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {notification.saleReceiptNumber && notification.amountRemaining != null
            ? t('meta.paymentPlanReceiptRemaining', {
                clientName: notification.clientName,
                receipt: notification.saleReceiptNumber,
                amount: formatCurrency(Math.round(notification.amountRemaining), 0),
              })
            : notification.saleReceiptNumber
              ? t('meta.paymentPlanReceipt', {
                  clientName: notification.clientName,
                  receipt: notification.saleReceiptNumber,
                })
              : notification.amountRemaining != null
                ? t('meta.paymentPlanRemaining', {
                    clientName: notification.clientName,
                    amount: formatCurrency(Math.round(notification.amountRemaining), 0),
                  })
                : t('meta.paymentPlan', {
                    clientName: notification.clientName,
                  })}
        </Text>
      );
    }
    return null;
  };

  return (
    <>
      <div style={{ marginTop: '8px' }}>
        <Badge 
          count={unreadCount} 
          size="default"
          offset={[-8, 8]}
          style={{ 
            backgroundColor: '#ff4d4f', // Rouge pour tous les types
            // Clignotement si des demandes de RDV non lues
            animation: unreadAppointmentRequests > 0 ? 'blink 1.5s ease-in-out infinite' : 'none',
            boxShadow: '0 0 0 1px #fff' // Bordure blanche pour meilleure visibilité
          }}
        >
        <Button 
          type="text" 
          icon={
            <BellOutlined 
              className={unreadCount > 0 ? 'notification-bell-shake' : ''}
              style={{
                fontSize: '24px',
                color: unreadCount > 0 ? '#ff4d4f' : '#8c8c8c', // Rouge si notifications
              }}
            />
          } 
          onClick={handleOpenDrawer}
          style={{ 
            width: '52px',
            height: '52px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      </Badge>
      </div>

      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <BellOutlined />
              <Text strong>Notifications</Text>
              {unreadCount > 0 && (
                <Badge count={unreadCount} size="small" />
              )}
            </Space>
            <Space>
              <Button 
                type="text" 
                icon={<CloseOutlined />} 
                onClick={handleCloseDrawer}
                size="small"
              />
            </Space>
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        open={drawerVisible}
        width={isNarrow ? '100%' : 420}
        className={className}
        styles={{
          body: { padding: 0 },
          header: { 
            borderBottom: '1px solid #f0f0f0',
            padding: isNarrow ? '12px 16px' : '16px 24px'
          }
        }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {showPushOptIn && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <Alert
                type="info"
                showIcon
                icon={<MobileOutlined />}
                message={t('push.enableTitle')}
                description={t('push.enableDesc')}
                action={
                  <Space direction="vertical" size={4}>
                    <Button
                      size="small"
                      type="primary"
                      loading={isRegistering}
                      onClick={() => {
                        void enablePush();
                      }}
                    >
                      {t('push.enableCta')}
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => {
                        setDrawerVisible(false);
                        navigate('/settings/notifications');
                      }}
                    >
                      {t('push.managePrefs')}
                    </Button>
                  </Space>
                }
              />
            </div>
          )}
          {!showPushOptIn && (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
              <Button
                type="link"
                size="small"
                icon={<MobileOutlined />}
                onClick={() => {
                  setDrawerVisible(false);
                  navigate('/settings/notifications');
                }}
              >
                {t('push.managePrefs')}
              </Button>
            </div>
          )}
          {permission === 'denied' && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <Alert type="warning" showIcon message={t('push.denied')} />
            </div>
          )}
          {/* Statistiques rapides */}
          {notifications.length > 0 && (
            <div style={{ 
              padding: isNarrow ? '12px 16px' : '16px 24px', 
              background: '#fafafa',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <Space size="large">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ff4d4f' }}>
                    {notifications.filter(n => n.severity === 'critical' && !n.read).length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Critiques</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#faad14' }}>
                    {notifications.filter(n => n.severity === 'warning' && !n.read).length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Avertissements</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                    {unreadCount}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Non lues</div>
                </div>
              </Space>
            </div>
          )}

          {/* Liste des notifications — layout empilé (pas de colonnes avatar / actions) */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {notifications.length > 0 ? (
              <List
                dataSource={notifications}
                split={false}
                renderItem={(notification) => {
                  const navigable = isNavigableNotification(notification.type);
                  const meta = renderNotificationMeta(notification);
                  return (
                    <List.Item
                      style={{
                        display: 'block',
                        padding: isNarrow ? '12px 16px' : '14px 20px',
                        backgroundColor: notification.read ? '#fff' : '#f6ffed',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: navigable ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (navigable) handleNotificationNavigate(notification);
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                          marginBottom: 6,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            lineHeight: '22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: 16,
                          }}
                          aria-hidden
                        >
                          {getNotificationIcon(notification.type)}
                        </span>
                        <Tag
                          color={getNotificationColor(notification.type)}
                          style={{
                            margin: 0,
                            fontSize: 12,
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            lineHeight: 1.35,
                          }}
                        >
                          {getNotificationTypeLabel(notification.type, t)}
                        </Tag>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {formatTime(notification.timestamp)}
                        </Text>
                        {!notification.read && (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              backgroundColor: '#1890ff',
                              borderRadius: '50%',
                              flexShrink: 0,
                              marginLeft: 'auto',
                            }}
                            aria-hidden
                          />
                        )}
                      </div>
                      <Text
                        style={{
                          display: 'block',
                          fontSize: 14,
                          fontWeight: notification.read ? 'normal' : 500,
                          lineHeight: 1.45,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {stripLeadingNotificationEmoji(getNotificationMessage(notification, t))}
                      </Text>
                      {meta && (
                        <div style={{ marginTop: 4 }}>{meta}</div>
                      )}
                    </List.Item>
                  );
                }}
              />
            ) : (
              <div style={{ 
                padding: '60px 20px', 
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Empty 
                  description={t('center.empty')} 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default NotificationCenter;
